import { MutationFunction } from '@apollo/client'
import { Autocomplete, TextField } from '@mui/material'
import { GetCandidatePreferencesQuery } from '__generated__/graphql'
import { FC, useState } from 'react'
import UpdatedText from 'shared/components/UpdatedText/UpdatedText'
import { useEffectOnlyAfterMount } from 'shared/hooks/useEffectAfterMount'
import { DropdownOption } from 'shared/models/dropdownOption'
import { getPxAsRem } from 'shared/utils'

interface LocationProps {
    locationOptions: DropdownOption[]
    updatedAt: string
    updatedBy: string
    handleUpdate: MutationFunction<any, any>
    candidateId?: string
    locationPreferences: NonNullable<
        GetCandidatePreferencesQuery['candidate']
    >['locationPreferences']
}

const Location: FC<LocationProps> = ({
    locationPreferences,
    locationOptions,
    updatedAt,
    updatedBy,
    handleUpdate,
    candidateId,
}) => {
    const [primaryLocation, setPrimaryLocation] = useState<DropdownOption[]>(
        locationPreferences
            ?.filter((location) => location.order === 1)
            .map((location) => location.preferenceLocation) || []
    )
    const [secondaryLocation, setSecondaryLocation] = useState<
        DropdownOption[]
    >(
        locationPreferences
            ?.filter((location) => location.order === 2)
            .map((location) => location.preferenceLocation) || []
    )
    const [tertiaryLocation, setTertiaryLocation] = useState<DropdownOption[]>(
        locationPreferences
            ?.filter((location) => location.order === 3)
            .map((location) => location.preferenceLocation) || []
    )

    const isOptionsDisabled = (option, selectedOptions): boolean => {
        return (
            selectedOptions?.length >= 3 &&
            !selectedOptions.some((item) => item.value === option.value)
        )
    }

    const handleSave = () => {
        handleUpdate({
            variables: {
                candidateId,
                preferences: [
                    ...primaryLocation.map((location) => ({
                        preferenceLocationId: location.id,
                        order: 1,
                    })),
                    ...secondaryLocation.map((location) => ({
                        preferenceLocationId: location.id,
                        order: 2,
                    })),
                    ...tertiaryLocation.map((location) => ({
                        preferenceLocationId: location.id,
                        order: 3,
                    })),
                ].filter((pref) => !!pref.preferenceLocationId),
            },
        })
    }

    useEffectOnlyAfterMount(() => {
        handleSave()
    }, [primaryLocation, secondaryLocation, tertiaryLocation])

    return (
        <>
            <Autocomplete
                fullWidth
                multiple
                getOptionDisabled={(option) =>
                    isOptionsDisabled(option, primaryLocation)
                }
                sx={{ marginBottom: getPxAsRem(10) }}
                isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                }
                onChange={(event, newValue) => {
                    setPrimaryLocation(newValue)
                }}
                value={primaryLocation}
                options={locationOptions.filter(
                    (option) =>
                        secondaryLocation
                            .concat(tertiaryLocation)
                            .map((loc) => loc.id)
                            .indexOf(option.id) === -1
                )}
                getOptionLabel={(option) => option.value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Primary Location"
                        placeholder=""
                    />
                )}
            />
            <Autocomplete
                fullWidth
                multiple
                getOptionDisabled={(option) =>
                    isOptionsDisabled(option, secondaryLocation)
                }
                sx={{ marginBottom: getPxAsRem(10) }}
                isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                }
                onChange={(event, newValue) => {
                    setSecondaryLocation(newValue)
                }}
                value={secondaryLocation}
                options={locationOptions.filter(
                    (option) =>
                        primaryLocation
                            .concat(tertiaryLocation)
                            .map((loc) => loc.id)
                            .indexOf(option.id) === -1
                )}
                getOptionLabel={(option) => option.value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Secondary Location"
                        placeholder=""
                    />
                )}
            />
            <Autocomplete
                fullWidth
                multiple
                getOptionDisabled={(option) =>
                    isOptionsDisabled(option, tertiaryLocation)
                }
                sx={{ marginBottom: getPxAsRem(10) }}
                isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                }
                onChange={(event, newValue) => {
                    setTertiaryLocation(newValue)
                }}
                value={tertiaryLocation}
                options={locationOptions.filter(
                    (option) =>
                        secondaryLocation
                            .concat(primaryLocation)
                            .map((loc) => loc.id)
                            .indexOf(option.id) === -1
                )}
                getOptionLabel={(option) => option.value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Tertiary Location"
                        placeholder=""
                    />
                )}
            />
            <UpdatedText updatedAt={updatedAt} updatedBy={updatedBy} />
        </>
    )
}

export default Location
